import FirebaseImageUpload from "network/Firebase/storage/FirebaseImageUpload";
import React, { useEffect, useRef, useState } from "react";
import ApiService from "../../network/ApiService";
import AuthService from "../../network/AuthService";
import FileAttachment from "./FileAttachment";

const ConversationDetail = ({ id, onBack }) => {
  const [conversation, setConversation] = useState(null);
  const [input, setInput] = useState("");
  const [attachments, setAttachments] = useState([]);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 500);
  const [team, setTeam] = useState(null);
  const [messageCount, setMessageCount] = useState(0);
  const [isUploading, setIsUploading] = useState(false);

  const authService = new AuthService();
  const apiService = new ApiService();
  const messagesEndRef = useRef(null);
  const messagesContainerRef = useRef(null);

  const formatDateTime = (dateString) => {
    if (!dateString) return "";
    const day = dateString.slice(8, 10);
    const month = dateString.slice(5, 7);
    const year = dateString.slice(0, 4);
    const time = dateString.slice(11, 16);
    return `${day}.${month}.${year} - ${time}`;
  };

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth <= 400);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const fetchConversation = async (shouldScroll = false) => {
    try {
      const teamID = authService.getTeamID();
      const authToken = authService.getAuthToken();

      if (teamID && authToken && id) {
        const response = await apiService.get(`chat/team/${teamID}`);
        const conv = response.find((c) => c.id === id);

        const teamResponse = await apiService.get(`teams/${teamID}`);
        setTeam(teamResponse);

        const newMessages = conv?.messages?.length || 0;
        setConversation(conv);

        if (newMessages > messageCount && shouldScroll) {
          scrollToBottom();
        }
        setMessageCount(newMessages);

        markUnreadMessagesAsRead(conv);
      }
    } catch (error) {
      console.error("Failed to load conversation", error);
    }
  };

  useEffect(() => {
    fetchConversation(true);
    const interval = setInterval(() => fetchConversation(false), 60000);
    return () => clearInterval(interval);
  }, [id]);

  const scrollToBottom = () => {
    if (messagesContainerRef.current) {
      messagesContainerRef.current.scrollTop =
        messagesContainerRef.current.scrollHeight;
    }
  };

  const markUnreadMessagesAsRead = (conv) => {
    const unreadAdminMessages = conv.messages.filter(
      (msg) => msg.sender_team === false && !msg.read
    );

    unreadAdminMessages.forEach((msg) => {
      if (!msg.read) {
        apiService
          .post(`chat/message/${msg.id}/read`, {})
          .then(() => {
            setConversation((prev) => ({
              ...prev,
              messages: prev.messages.map((m) =>
                m.id === msg.id ? { ...m, read: true } : m
              ),
            }));
          })
          .catch((error) =>
            console.error("Failed to mark admin message as read:", error)
          );
      }
    });
  };

  const handleUploadStart = () => {
    setIsUploading(true);
  };

  const handleUploadSuccess = (url, name) => {
    setIsUploading(false);
    if (!url) {
      console.error("Missing URL in upload object", url);
      return;
    }

    const newAttachment = { name, url };
    setAttachments((prev) => [...prev, newAttachment]);
  };

  const handleSend = async () => {
    if (!input.trim() && attachments.length === 0) return;

    const teamID = authService.getTeamID();
    const authToken = authService.getAuthToken();
    if (!teamID || !authToken) {
      console.error("Missing authentication details.");
      return;
    }

    const messagePayload = {
      sender_team: true,
      sender_name: team.teamName,
      text: input,
      read: false,
      attachments: attachments,
    };

    try {
      const response = await apiService.post(
        `chat/${conversation.id}/message`,
        messagePayload
      );

      const newMessage = response.message || {
        id: response.id,
        ...messagePayload,
      };

      setConversation((prev) => ({
        ...prev,
        messages: [...prev.messages, newMessage],
      }));

      setInput("");
      setAttachments([]);
      fetchConversation(true);
    } catch (error) {
      console.error("Error sending message:", error);
    }
  };

  if (!conversation) return <div>Loading…</div>;

  return (
    <div
      style={{ padding: "20px", paddingBottom: isMobile ? "140px" : "20px" }}
    >
      <button
        style={{
          marginBottom: "20px",
          backgroundColor: "orange",
          color: "black",
          padding: "8px 16px",
          borderRadius: "4px",
          border: "none",
          cursor: "pointer",
        }}
        onClick={onBack}
      >
        ← Zurück zur Übersicht
      </button>

      <h2 style={{ textDecoration: "underline", marginBottom: "10px" }}>
        Betreff: {conversation.subject}
      </h2>

      <div
        ref={messagesContainerRef}
        style={{
          marginBottom: "20px",
          maxHeight: "60vh",
          overflowY: "auto",
          display: "flex",
          flexDirection: "column",
        }}
      >
        {conversation.messages.map((msg) => (
          <div
            key={msg.id}
            style={{
              alignSelf: msg.sender_team ? "flex-end" : "flex-start",
              backgroundColor: msg.sender_team ? "#e0f7fa" : "#ffe0b2",
              padding: "10px",
              color: "black",
              margin: "10px 0",
              borderRadius: "5px",
              maxWidth: "70%",
              textAlign: msg.sender_team ? "right" : "left",
              marginLeft: msg.sender_team ? "auto" : 0,
              marginRight: msg.sender_team ? 0 : "auto",
            }}
          >
            <strong>{msg.sender_name}</strong>
            <div style={{ whiteSpace: "pre-wrap" }}>{msg.text}</div>

            {msg.attachments?.length > 0 && (
              <div
                style={{
                  display: "flex",
                  flexWrap: "wrap",
                  justifyContent: msg.sender_team ? "flex-end" : "flex-start",
                  marginTop: 8,
                  gap: 8,
                }}
              >
                {msg.attachments.map((att) => (
                  <FileAttachment
                    key={att.url}
                    url={att.url}
                    filename={att.name}
                  />
                ))}
              </div>
            )}

            <small>
            {formatDateTime(msg.created)}
            </small>
          </div>
        ))}
        <div ref={messagesEndRef} />
      </div>

      <div
        style={
          isMobile
            ? {
                position: "fixed",
                bottom: 0,
                left: 0,
                width: "100%",
                padding: "10px 20px",
                boxShadow: "0 -2px 10px rgba(0,0,0,0.1)",
                boxSizing: "border-box",
                backgroundColor: "white",
              }
            : {}
        }
      >
    {conversation.open === true && conversation.id && (
    <div style={{ marginTop: "30px", gap: "10px", display: "flex", flexDirection: "column"}}>
        <FirebaseImageUpload
        onUploadStart={handleUploadStart}
        onUploadSuccess={handleUploadSuccess}
        path={`conversations/${conversation.id}/files`}
        filename={Date.now().toString()}
        buttonText="Datei Hochladen"
        />

    <textarea
          rows={isMobile ? 2 : 4}
          style={{
            width: "100%",
            padding: "10px",
            fontSize: "16px",
            color: "black",
            backgroundColor: "white",
            borderRadius: "8px",
            marginBottom: "8px",
            minHeight: "100px",
          }}
          value={input}
          onChange={(e) => setInput(e.target.value)}
          placeholder="Nachricht schreiben..."
        />

        <button
          style={{
            width: "100%",
            padding: "10px 20px",
            backgroundColor: "orange",
            border: "none",
            color: "#fff",
            fontSize: "16px",
            borderRadius: "5px",
            cursor: isUploading ? "not-allowed" : "pointer",
            opacity: isUploading ? 0.6 : 1,
          }}
          onClick={handleSend}
          disabled={isUploading || (!input.trim() && attachments.length === 0)}
        >
          {isUploading ? "Warte auf Upload…" : "Senden"}
        </button>
    </div>
    )}
      </div>
    </div>
  );
};

export default ConversationDetail;
